/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: APR.ANALYTICS
 *
 * Read-only approval analytics over the engine's metric views (E4):
 *   apr_config_metrics_v   — per-config throughput + avg cycle time
 *   apr_approver_workload_v — per-approver pending / decided counts
 * Views are security_invoker, so RLS scopes what each user sees.
 */

import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

interface ConfigMetric {
  approval_config_id: number; config_code: string; entity_name: string;
  total: number; pending: number; approved: number; rejected: number; avg_cycle_seconds: number | null;
}
interface Workload { approver_user_id: number; pending_steps: number; approved_steps: number; rejected_steps: number; }

function humanSeconds(s: number | null): string {
  if (s == null) return '—';
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

const CONFIG_GRID_COLS: GridColumnDef[] = [
  { key: 'config_code',       header: 'Config',    defaultWidth: 200 },
  { key: 'entity_name',       header: 'Entity',    defaultWidth: 200 },
  { key: 'total',             header: 'Total',     defaultWidth: 90  },
  { key: 'pending',           header: 'Pending',   defaultWidth: 90  },
  { key: 'approved',          header: 'Approved',  defaultWidth: 90  },
  { key: 'rejected',          header: 'Rejected',  defaultWidth: 90  },
  { key: 'avg_cycle_seconds', header: 'Avg cycle', defaultWidth: 110 },
];

/** Numeric grid columns rendered right-aligned with tabular figures. */
const CONFIG_NUM_COLS = new Set(['total', 'pending', 'approved', 'rejected', 'avg_cycle_seconds']);
const WORKLOAD_NUM_COLS = new Set(['pending_steps', 'approved_steps', 'rejected_steps']);

const WORKLOAD_GRID_COLS: GridColumnDef[] = [
  { key: 'approver',         header: 'Approver', defaultWidth: 220 },
  { key: 'pending_steps',    header: 'Pending',            defaultWidth: 90  },
  { key: 'approved_steps',   header: 'Approved',           defaultWidth: 90  },
  { key: 'rejected_steps',   header: 'Rejected',           defaultWidth: 90  },
];

export interface ApprovalAnalyticsPageProps { session: AuthSession; }

export function ApprovalAnalyticsPage({ session }: ApprovalAnalyticsPageProps) {
  return (
    <PersonalizationProvider pageCode="APR.ANALYTICS" session={session}>
      <ApprovalAnalyticsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ApprovalAnalyticsPageInner({ session }: ApprovalAnalyticsPageProps) {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ConfigMetric[]>([]);
  const [workload, setWorkload] = useState<Workload[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approverNames, setApproverNames] = useState<Record<number, string>>({});
  const detailCfg = useExpandedRows<number>();
  const detailWl  = useExpandedRows<number>();

  // §17.B13 related children (expand/contract). These are read-only metric-view
  // rows (aggregate counts) with no per-row child collection loaded, so the control
  // renders inert.
  // TODO: wire relations
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [c, w] = await Promise.all([
        apiDb.from('apr_config_metrics_v').select('approval_config_id, config_code, entity_name, total, pending, approved, rejected, avg_cycle_seconds').order('total', { ascending: false }),
        apiDb.from('apr_approver_workload_v').select('approver_user_id, pending_steps, approved_steps, rejected_steps').order('pending_steps', { ascending: false }),
      ]);
      if (c.error) throw c.error;
      if (w.error) throw w.error;
      setConfigs((c.data ?? []) as ConfigMetric[]);
      const wl = (w.data ?? []) as Workload[];
      setWorkload(wl);
      const ids = Array.from(new Set(wl.map(x => x.approver_user_id).filter((x): x is number => x != null)));
      if (ids.length) {
        const { data: us } = await apiDb.from('csm_user_master').select('user_id, display_name, email').in('user_id', ids);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) m[u.user_id] = u.display_name || u.email || '—';
        setApproverNames(m);
      } else { setApproverNames({}); }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('apr_analytics.title', 'Approval Analytics')}</span>
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('apr_analytics.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('apr_analytics.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('apr_analytics.load_failed', "Couldn't load approval analytics.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <h2 className="mb-1 text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apr_analytics.by_configuration', 'By configuration')}</h2>
        <PersonalizedGrid componentKey="grid" columns={CONFIG_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={`mb-6 ${GRID_TABLE_CLASS}`}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apr_analytics.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className={`border-b border-csm-border px-2 py-1 text-left${CONFIG_NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {configs.map(c => (
                  <Fragment key={c.approval_config_id}>
                  <tr className="border-t border-csm-border-soft">
                    <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detailCfg.isOpen(c.approval_config_id)} hasChildren={hasRelated(c.approval_config_id)} onToggle={() => detailCfg.toggle(c.approval_config_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className={`px-2 py-1${CONFIG_NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'config_code'       && <span className="font-mono text-csm-sm">{c.config_code}</span>/* fk-display-ok: the analytics row's own APR config identifier (no separate name) */}
                        {col.key === 'entity_name'       && <span className="font-mono text-csm-xs">{c.entity_name}</span>}
                        {col.key === 'total'             && c.total}
                        {col.key === 'pending'           && <span className="text-csm-warning">{c.pending}</span>}
                        {col.key === 'approved'          && <span className="text-csm-success">{c.approved}</span>}
                        {col.key === 'rejected'          && <span className="text-csm-danger">{c.rejected}</span>}
                        {col.key === 'avg_cycle_seconds' && humanSeconds(c.avg_cycle_seconds)}
                      </td>
                    ))}
                  </tr>
                  {detailCfg.isOpen(c.approval_config_id) && (
                    <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(c.approval_config_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {configs.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-3"><EmptyState variant="no-data" title={t('apr_analytics.no_data', 'No data.')} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>

        <h2 className="mb-1 text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apr_analytics.approver_workload', 'Approver workload')}</h2>
        <PersonalizedGrid componentKey="workload_grid" columns={WORKLOAD_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apr_analytics.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className={`border-b border-csm-border px-2 py-1 text-left${WORKLOAD_NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workload.map(w => (
                  <Fragment key={w.approver_user_id}>
                  <tr className="border-t border-csm-border-soft">
                    <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detailWl.isOpen(w.approver_user_id)} hasChildren={hasRelated(w.approver_user_id)} onToggle={() => detailWl.toggle(w.approver_user_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className={`px-2 py-1${WORKLOAD_NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'approver'         && (approverNames[w.approver_user_id] ?? '—')}
                        {col.key === 'pending_steps'    && <span className="text-csm-warning">{w.pending_steps}</span>}
                        {col.key === 'approved_steps'   && <span className="text-csm-success">{w.approved_steps}</span>}
                        {col.key === 'rejected_steps'   && <span className="text-csm-danger">{w.rejected_steps}</span>}
                      </td>
                    ))}
                  </tr>
                  {detailWl.isOpen(w.approver_user_id) && (
                    <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(w.approver_user_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {workload.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-3"><EmptyState variant="no-data" title={t('apr_analytics.no_data', 'No data.')} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>
    </div>
  );
}
